import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/stores/authStore';
import { UserProfile, useAuth, useClerk, useUser } from '@clerk/clerk-react';
import {
  Bell,
  Building2,
  Camera,
  DollarSign,
  Eye,
  Key,
  Loader2,
  Save,
  Shield,
  ShoppingCart,
  User,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function Settings() {
  const { user: authUser } = useAuthStore();
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const { getToken } = useAuth();
  const clerk = useClerk();

  // Profile editing state
  const [name, setName] = useState(authUser?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  // Payout information state (for agents)
  const [payoutDetails, setPayoutDetails] = useState('');
  const [isSavingPayout, setIsSavingPayout] = useState(false);

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    is_discoverable: false,
    show_scores_publicly: true,
    allow_friend_requests: true,
    email_notifications: true,
    push_notifications: true,
  });
  const [loadingPrivacy, setLoadingPrivacy] = useState(true);
  const [savingPrivacy, setSavingPrivacy] = useState<string | null>(null);

  // Fetch privacy settings on mount
  useEffect(() => {
    const fetchPrivacySettings = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api'}/settings`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.data) {
            setPrivacySettings(data.data);
          }
        }
      } catch (err) {
        console.error('Error fetching privacy settings:', err);
      } finally {
        setLoadingPrivacy(false);
      }
    };

    fetchPrivacySettings();
  }, [getToken]);

  const updatePrivacySetting = async (key: string, value: boolean) => {
    setSavingPrivacy(key);
    try {
      const token = await getToken();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api'}/settings`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ [key]: value }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          setPrivacySettings(data.data);
        }
        toast.success('Setting updated');
      } else {
        toast.error('Failed to update setting');
      }
    } catch (err) {
      console.error('Error updating privacy setting:', err);
      toast.error('Failed to update setting');
    } finally {
      setSavingPrivacy(null);
    }
  };

  // Check user roles for conditional sections
  const isAgent = authUser?.role === 'agent';
  const isAffiliate = authUser?.role === 'affiliate_owner' || authUser?.role === 'affiliate_coach';

  // Placeholder data for Recent Sales (for agents)
  const recentSales: Array<{
    date: string;
    product: string;
    customer: string;
    saleAmount: number;
    commission: number;
  }> = [];

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setIsSaving(true);
    try {
      // Update in Clerk
      if (clerkUser) {
        await clerkUser.update({
          firstName: name.split(' ')[0],
          lastName: name.split(' ').slice(1).join(' ') || undefined,
        });
      }

      // Update in D1 via API
      const token = await getToken();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api'}/auth/me`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: name.trim() }),
        }
      );

      if (response.ok) {
        toast.success('Profile updated successfully');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageAccount = () => {
    clerk.openUserProfile();
  };

  const handleSavePayout = async () => {
    setIsSavingPayout(true);
    try {
      // TODO: Implement payout details save API
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API call
      toast.success('Payout information saved');
    } catch (err) {
      console.error('Error saving payout info:', err);
      toast.error('Failed to save payout information');
    } finally {
      setIsSavingPayout(false);
    }
  };

  const roleLabels: Record<string, string> = {
    hq_admin: 'HQ Admin',
    agent: 'Agent',
    affiliate_owner: 'Affiliate Owner',
    affiliate_coach: 'Affiliate Coach',
    member: 'Member',
    individual: 'Individual',
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Settings</h1>
        <p className="mt-1 text-muted-foreground">Manage your account settings and preferences.</p>
      </div>

      <div className="space-y-6">
        {/* Row 1: Profile + Account Info */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Profile Section */}
          <Card className="animate-fade-in">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Profile
              </CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-primary text-xl font-bold">
                    {(authUser?.username || name).charAt(0).toUpperCase() || '?'}
                  </div>
                  <button
                    type="button"
                    className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={handleManageAccount}
                    title="Change avatar via Clerk"
                  >
                    <Camera className="h-3 w-3" />
                  </button>
                </div>
                <div>
                  <p className="font-medium">@{authUser?.username || 'user'}</p>
                  <p className="text-sm text-muted-foreground">{authUser?.email}</p>
                </div>
              </div>

              {/* Name Field */}
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={authUser?.username ? `@${authUser.username}` : ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Your public username cannot be changed.
                </p>
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" value={authUser?.email || ''} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">
                  Email changes require verification via Clerk.
                </p>
              </div>

              {/* Save Button */}
              <Button onClick={handleSaveProfile} disabled={isSaving} className="w-full sm:w-auto">
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">User ID</span>
                  <span className="font-mono text-xs">{authUser?.id?.slice(0, 16)}...</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Role</span>
                  <Badge variant="secondary">
                    {roleLabels[authUser?.role || ''] || authUser?.role}
                  </Badge>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Authentication</span>
                  <span className="flex items-center gap-2">
                    {clerkLoaded ? (
                      <>
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        Connected
                      </>
                    ) : (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading...
                      </>
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className="bg-green-600">Active</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Security + Notifications */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Security Section */}
          <Card className="animate-fade-in" style={{ animationDelay: '100ms' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                Security
              </CardTitle>
              <CardDescription>Password and authentication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Update password, enable 2FA, and manage security settings.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowSecurityModal(true)}
                className="w-full"
              >
                <Key className="mr-2 h-4 w-4" />
                Manage Security
              </Button>
            </CardContent>
          </Card>

          {/* Notifications Section */}
          <Card className="animate-fade-in" style={{ animationDelay: '150ms' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                Notifications
              </CardTitle>
              <CardDescription>How you receive updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingPrivacy ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Email Notifications */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="email-notif" className="font-medium">
                        Email
                      </Label>
                      <p className="text-xs text-muted-foreground">Workouts & announcements</p>
                    </div>
                    <button
                      id="email-notif"
                      type="button"
                      role="switch"
                      aria-checked={privacySettings.email_notifications}
                      onClick={() =>
                        updatePrivacySetting(
                          'email_notifications',
                          !privacySettings.email_notifications
                        )
                      }
                      disabled={savingPrivacy === 'email_notifications'}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${privacySettings.email_notifications ? 'bg-primary' : 'bg-muted'} ${savingPrivacy === 'email_notifications' ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow transition ${privacySettings.email_notifications ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>

                  {/* Push Notifications */}
                  <div className="flex items-center justify-between border-t border-border pt-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="push-notif" className="font-medium">
                        Push
                      </Label>
                      <p className="text-xs text-muted-foreground">Real-time alerts</p>
                    </div>
                    <button
                      id="push-notif"
                      type="button"
                      role="switch"
                      aria-checked={privacySettings.push_notifications}
                      onClick={() =>
                        updatePrivacySetting(
                          'push_notifications',
                          !privacySettings.push_notifications
                        )
                      }
                      disabled={savingPrivacy === 'push_notifications'}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${privacySettings.push_notifications ? 'bg-primary' : 'bg-muted'} ${savingPrivacy === 'push_notifications' ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow transition ${privacySettings.push_notifications ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 3: Privacy - Full Width with Horizontal Layout */}
        <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Privacy
            </CardTitle>
            <CardDescription>Control your profile visibility</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingPrivacy ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-3">
                {/* Profile Discoverability */}
                <div className="flex items-center justify-between md:flex-col md:items-start md:gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="discoverable" className="font-medium">
                        Discoverable
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">Others can find you</p>
                  </div>
                  <button
                    id="discoverable"
                    type="button"
                    role="switch"
                    aria-checked={privacySettings.is_discoverable}
                    onClick={() =>
                      updatePrivacySetting('is_discoverable', !privacySettings.is_discoverable)
                    }
                    disabled={savingPrivacy === 'is_discoverable'}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${privacySettings.is_discoverable ? 'bg-primary' : 'bg-muted'} ${savingPrivacy === 'is_discoverable' ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow transition ${privacySettings.is_discoverable ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>

                {/* Show Scores Publicly */}
                <div className="flex items-center justify-between md:flex-col md:items-start md:gap-3 md:border-l md:border-border md:pl-6">
                  <div className="space-y-1">
                    <Label htmlFor="scores" className="font-medium">
                      Show Scores
                    </Label>
                    <p className="text-xs text-muted-foreground">On leaderboards</p>
                  </div>
                  <button
                    id="scores"
                    type="button"
                    role="switch"
                    aria-checked={privacySettings.show_scores_publicly}
                    onClick={() =>
                      updatePrivacySetting(
                        'show_scores_publicly',
                        !privacySettings.show_scores_publicly
                      )
                    }
                    disabled={savingPrivacy === 'show_scores_publicly'}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${privacySettings.show_scores_publicly ? 'bg-primary' : 'bg-muted'} ${savingPrivacy === 'show_scores_publicly' ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow transition ${privacySettings.show_scores_publicly ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>

                {/* Allow Friend Requests */}
                <div className="flex items-center justify-between md:flex-col md:items-start md:gap-3 md:border-l md:border-border md:pl-6">
                  <div className="space-y-1">
                    <Label htmlFor="friends" className="font-medium">
                      Friend Requests
                    </Label>
                    <p className="text-xs text-muted-foreground">Allow connections</p>
                  </div>
                  <button
                    id="friends"
                    type="button"
                    role="switch"
                    aria-checked={privacySettings.allow_friend_requests}
                    onClick={() =>
                      updatePrivacySetting(
                        'allow_friend_requests',
                        !privacySettings.allow_friend_requests
                      )
                    }
                    disabled={savingPrivacy === 'allow_friend_requests'}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${privacySettings.allow_friend_requests ? 'bg-primary' : 'bg-muted'} ${savingPrivacy === 'allow_friend_requests' ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow transition ${privacySettings.allow_friend_requests ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agent Sections - Side by Side */}
        {isAgent && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Payout Information */}
            <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  Payout Information
                </CardTitle>
                <CardDescription>Bank details for commissions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="payout">Bank Account Details</Label>
                  <Textarea
                    id="payout"
                    value={payoutDetails}
                    onChange={(e) => setPayoutDetails(e.target.value)}
                    placeholder="Enter your bank account details..."
                    rows={3}
                  />
                </div>
                <Button onClick={handleSavePayout} disabled={isSavingPayout} className="w-full">
                  {isSavingPayout ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Payout Info
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Recent Sales */}
            <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                  Recent Sales
                </CardTitle>
                <CardDescription>Commission-earning transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {recentSales.length === 0 ? (
                  <div className="text-center py-6">
                    <ShoppingCart className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No recent sales</p>
                    <p className="text-xs text-muted-foreground">Sales will appear here.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentSales.map((sale) => (
                        <TableRow key={`${sale.date}-${sale.product}-${sale.customer}`}>
                          <TableCell>{sale.date}</TableCell>
                          <TableCell>{sale.product}</TableCell>
                          <TableCell className="text-right text-green-500">
                            ${sale.commission.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Affiliate Sections - Side by Side */}
        {isAffiliate && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Affiliate Information */}
            <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Affiliate Information
                </CardTitle>
                <CardDescription>Your gym details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Gym Name</span>
                    <span>Your Gym</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Role</span>
                    <span>{authUser?.role === 'affiliate_owner' ? 'Owner' : 'Coach'}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">Status</span>
                    <Badge variant="default">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Affiliate Status */}
            <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
              <CardHeader>
                <CardTitle>Subscription Status</CardTitle>
                <CardDescription>Tier and benefits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Current Tier</span>
                    <Badge variant="secondary">Standard</Badge>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Member Limit</span>
                    <span>Unlimited</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">Status</span>
                    <Badge className="bg-green-600">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Security Modal with Clerk UserProfile */}
      {showSecurityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-10 bg-background/80 hover:bg-background"
              onClick={() => setShowSecurityModal(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            <UserProfile
              appearance={{
                baseTheme: undefined,
                variables: {
                  colorPrimary: '#FFBB38',
                  colorBackground: '#1A1A1A',
                  colorText: '#FAFAFA',
                  colorTextSecondary: '#9CA3AF',
                  colorInputBackground: '#141414',
                  colorInputText: '#FAFAFA',
                  borderRadius: '0.5rem',
                },
                elements: {
                  rootBox: 'w-full',
                  card: 'bg-card border border-border shadow-xl rounded-lg',
                  navbar: 'hidden',
                  navbarMobileMenuButton: 'hidden',
                  headerTitle: 'text-foreground',
                  headerSubtitle: 'text-muted-foreground',
                  profileSectionTitleText: 'text-foreground',
                  formFieldLabel: 'text-foreground',
                  formFieldInput: 'bg-background border-border text-foreground',
                  formButtonPrimary: 'bg-primary text-primary-foreground hover:bg-primary/90',
                },
              }}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
